import {
  Aggregation,
  BuiltInDimension,
  DimensionSettings,
  Measure,
  SortOrder,
  SortType,
  UniqueAggregation,
} from '@fullstory/proto/fs/api/metrics';
import { Scope } from '@fullstory/proto/fs/api/search';
import { DependentEventFilter, TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import { AuditMetadata } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { MetricDetail, UnnamedMetric } from '@fullstory/proto/fs/services/dx-objects';
import {
  Color,
  SegmentSettings,
  TimeRangeComparison,
  TrendFrequency,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { Display, DisplayPrecision } from '@fullstory/proto/fs/services/webber/frontend/display';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { mockAuditMetadata } from '../shared/metadata';

export const DEFAULT_METRIC_ID = 'test';
export const DEFAULT_METRIC_NAME = 'Test Metric';
export const DEFAULT_DESCRIPTION = 'This is a test metric';
export const DEFAULT_TIME_RANGE = {
  endTime: 'NOW/DAY+1DAY',
  startTime: 'NOW/DAY-29DAY',
};
export const DEFAULT_DISPLAY: Display = {
  number: true,
  displayPrecision: DisplayPrecision.PRECISION_0,
};

const mockSegmentSettings = ({
  segmentId = 'everyone',
  segmentScope = Scope.SCOPE_INDIVIDUAL,
}: {
  segmentId?: string;
  segmentScope?: Scope;
}) => ({
  segmentId,
  segmentScope,
});

export interface MockMetricDetailParams {
  name?: string;
  aggregation?: Aggregation;
  lastViewed?: timestamp | null;
  metricId?: string;
  metadata?: Partial<AuditMetadata>;
  starred?: boolean;
  segmentScope?: Scope;
  dimensionSettings?: DimensionSettings | null;
  deps?: DependentEventFilter[];
  description?: string;
  displayAs?: Display;
  timeRange?: TimeRange;
  segment?: SegmentSettings[];
  timeRangeComparison?: TimeRangeComparison | null;
  frequency?: TrendFrequency;
  computePast?: boolean;
}

export const defaultDimensionSettingsBrowser: DimensionSettings = {
  sortOrder: SortOrder.SORT_ORDER_DESC,
  sortByDefault: false,
  sortType: SortType.SORT_TYPE_AGGREGATION,
  limit: 50,
  dimension: {
    prefix: '',
    builtInProperty: BuiltInDimension.DIMENSION_BROWSER,
    limit: 0,
    computeAtLevel: false,
    eventRefinement: null,
  },
  dimensions: [
    {
      prefix: '',
      builtInProperty: BuiltInDimension.DIMENSION_BROWSER,
      limit: 0,
      computeAtLevel: false,
      eventRefinement: null,
    },
  ],
  computeTopLevel: false,
  computeNumGroups: false,
  allowAllPartialResults: false,
  noFallback: false,
};

export const mockMetricDetail = ({
  name = DEFAULT_METRIC_NAME,
  aggregation = { default: {} },
  lastViewed = Timestamp.now(),
  metricId = DEFAULT_METRIC_ID,
  metadata,
  starred = true,
  deps = [],
  description = DEFAULT_DESCRIPTION,
}: MockMetricDetailParams): MetricDetail => ({
  metric: {
    metric: {
      id: metricId,
      name,
      description,
      metric: {
        single: {
          event: {
            primary: null,
            deps,
          },
          aggregation,
          rollingWindow: 0,
          rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
          rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
        },
      },
      funnelFilter: null,
    },
    metadata: mockAuditMetadata(metadata),
    isDcmCreated: false,
    isCanned: false,
  },
  starred,
  lastViewed,
  canned: false,
});

export const mockUnnamedMetric = (name: string = DEFAULT_METRIC_NAME): UnnamedMetric => ({
  metric: {
    single: {
      event: {
        primary: null,
        deps: [],
      },
      aggregation: {
        unique: UniqueAggregation.UNIQUE_AGGREGATION_SESSION,
      },
      rollingWindow: 0,
      rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
      rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
    },
  },
  segmentIds: [],
  segments: [],
  dimensionSettings: {
    sortOrder: SortOrder.SORT_ORDER_DESC,
    sortByDefault: false,
    sortType: SortType.SORT_TYPE_AGGREGATION,
    limit: 50,
    dimension: {
      prefix: '',
      builtInProperty: BuiltInDimension.DIMENSION_BROWSER,
      limit: 0,
      computeAtLevel: false,
      eventRefinement: null,
    },
    dimensions: [
      {
        prefix: '',
        builtInProperty: BuiltInDimension.DIMENSION_BROWSER,
        limit: 0,
        computeAtLevel: false,
        eventRefinement: null,
      },
    ],
    computeTopLevel: false,
    computeNumGroups: false,
    allowAllPartialResults: false,
    noFallback: false,
  },
  timeInterval: TimeInterval.TIME_INTERVAL_DAY,
  display: DEFAULT_DISPLAY,
  timeRange: DEFAULT_TIME_RANGE,
  timeRangeComparison: null,
  name,
  description: '',
  singleNumberColor: Color.COLOR_INVALID,
});

export const mockSingleNumberMetric = ({
  metricId = DEFAULT_METRIC_ID,
  name = DEFAULT_METRIC_NAME,
  description = DEFAULT_DESCRIPTION,
  aggregation = { default: {} },
  segmentScope = Scope.SCOPE_INDIVIDUAL,
  lastViewed = Timestamp.now(),
  metadata,
  starred = true,
  deps = [],
  displayAs = DEFAULT_DISPLAY,
  timeRange = DEFAULT_TIME_RANGE,
  segment = [mockSegmentSettings({ segmentScope })],
  timeRangeComparison = null,
  computePast = false,
}: MockMetricDetailParams): MetricDetail => {
  const baseMetric = mockMetricDetail({
    description,
    name,
    aggregation,
    lastViewed,
    metricId,
    metadata,
    starred,
    deps,
  });
  return {
    ...baseMetric,
    metric: {
      ...baseMetric.metric,
      metric: {
        ...baseMetric.metric!.metric!,
        singleNumber: {
          segmentId: [],
          computePast,
          displayAs,
          timeRange,
          timeRangeComparison,
          segment: segment[0]!,
          conversionId: [],
          color: Color.COLOR_INVALID,
        },
      },
      metadata: mockAuditMetadata(metadata),
      isDcmCreated: false,
      isCanned: false,
    },
  };
};

export const mockDimensionalityMetric = ({
  name = DEFAULT_METRIC_NAME,
  aggregation = { default: {} },
  segmentScope = Scope.SCOPE_INDIVIDUAL,
  lastViewed = Timestamp.now(),
  metricId = DEFAULT_METRIC_ID,
  metadata,
  starred = true,
  dimensionSettings = defaultDimensionSettingsBrowser,
  deps = [],
  displayAs = DEFAULT_DISPLAY,
  timeRange = DEFAULT_TIME_RANGE,
  segment = [mockSegmentSettings({ segmentScope })],
  description,
}: MockMetricDetailParams): MetricDetail => {
  const baseMetric = mockMetricDetail({
    description,
    name,
    aggregation,
    lastViewed,
    metricId,
    metadata,
    starred,
    deps,
  });
  return {
    ...baseMetric,
    metric: {
      ...baseMetric.metric,
      metric: {
        ...baseMetric.metric!.metric!,
        dimensionality: {
          segmentId: [],
          computePast: false,
          displayAs,
          timeRange,
          timeRangeComparison: null,
          segment: segment[0]!,
          dimensionSettings,
          color: Color.COLOR_INVALID,
        },
      },
      metadata: mockAuditMetadata(),
      isDcmCreated: false,
      isCanned: false,
    },
  };
};

export const mockTrendMetric = ({
  name = DEFAULT_METRIC_NAME,
  description = DEFAULT_DESCRIPTION,
  aggregation = { default: {} },
  segmentScope = Scope.SCOPE_INDIVIDUAL,
  lastViewed = Timestamp.now(),
  metricId = DEFAULT_METRIC_ID,
  metadata,
  starred = true,
  dimensionSettings = null,
  deps = [],
  displayAs = DEFAULT_DISPLAY,
  timeRange = DEFAULT_TIME_RANGE,
  segment = [mockSegmentSettings({ segmentScope })],
  frequency = TrendFrequency.TREND_FREQUENCY_DAILY,
}: MockMetricDetailParams): MetricDetail => {
  const baseMetric = mockMetricDetail({
    name,
    description,
    aggregation,
    lastViewed,
    metricId,
    metadata,
    starred,
    deps,
  });
  return {
    ...baseMetric,
    metric: {
      ...baseMetric.metric,
      metric: {
        ...baseMetric.metric!.metric!,
        trend: {
          segmentId: [],
          computePast: false,
          displayAs,
          timeRange,
          timeRangeComparison: null,
          segment,
          conversionId: [],
          frequency,
          dimensionSettings,
          color: Color.COLOR_INVALID,
        },
      },
      metadata: mockAuditMetadata(metadata),
      isDcmCreated: false,
      isCanned: false,
    },
  };
};
